// frontend/src/components/formularios/FormularioParteDiario.jsx
// FORMULARIO PARA CREAR/EDITAR PARTES DIARIOS
import React, { useEffect, useMemo, useState } from 'react';
import { toLocalYMD } from '../../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import FormularioBase from './FormularioBase';
import apiClient from '../../services/apiClient';
import { fetchCentroCostoId, getCentroCostoPreferidoDesdeEquipo, getObraId } from '../../utils/centroCosto';

const FormularioParteDiario = ({ parteDiario = null, initialEquipo = null, showTitle = true, noNestedScroll = false, compact = false, fullWidth = true, onSave: parentOnSave, onCancel: parentOnCancel, enableSaveAndNew = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id || parteDiario);

  const [initialData, setInitialData] = useState({});
  const [equipoMeta, setEquipoMeta] = useState(null); // detalles del equipo seleccionado

  const hoyISO = useMemo(() => toLocalYMD(new Date()), []);

  // Campos del formulario basados en el modelo ParteDiario (modelo backend)
  const fields = useMemo(() => {
    const sinContador = equipoMeta && equipoMeta.tiene_contador === false;
    const unidad = (equipoMeta?.contador_nombre || '').toLowerCase().startsWith('h') ? 'Hs' : 'Km';
    return [
      {
        name: 'equipo',
        label: 'Equipo',
        type: 'select',
        optionsEndpoint: 'equipos/',
        required: true,
        searchable: true,
        searchRemote: true,
        fkPath: '/equipos/crear',
        preserveOnDuplicate: true,
      },
      { name: 'fecha', label: 'Fecha', type: 'date', required: true },
      // Campo unificado de lectura según contador del equipo
      {
        name: 'lectura_contador',
        label: sinContador ? 'Este equipo no tiene contador — no se le pueden cargar partes diarios' : `Lectura (${unidad})`,
        type: 'number',
        required: true,
      },
      {
        name: 'obra',
        label: 'Obra',
        type: 'select',
        optionsEndpoint: 'obras/',
        required: false,
        searchable: true,
        searchRemote: true,
        fkConfig: { apiEndpoint: 'obras', titulo: 'Obras', descripcion: 'Catálogo de obras', icon: 'fas fa-hard-hat' },
        preserveOnDuplicate: true,
      },
      {
        name: 'sector',
        label: 'Sector',
        type: 'select',
        optionsEndpoint: 'sectores-trabajo/',
        required: false,
        searchable: true,
        searchRemote: true,
        fkConfig: { apiEndpoint: 'sectores-trabajo', titulo: 'Sectores de Trabajo', descripcion: 'Catálogo de sectores', icon: 'fas fa-sitemap' },
        preserveOnDuplicate: true,
      },
      {
        name: 'centro_costo',
        label: 'Centro de Costo',
        type: 'select',
        optionsEndpoint: 'centroscosto/',
        required: true,
        searchable: true,
        searchRemote: true,
        fkConfig: { apiEndpoint: 'centroscosto', titulo: 'Centros de Costo', descripcion: 'Catálogo de centros de costo', icon: 'fas fa-calculator' },
        preserveOnDuplicate: true,
      },
      { name: 'operador', label: 'Operador', type: 'text', required: true, preserveOnDuplicate: true },
      { name: 'tareas', label: 'Tareas', type: 'textarea', fullWidth: true },
      { name: 'observaciones', label: 'Observaciones', type: 'textarea', fullWidth: true }
    ];
  }, [equipoMeta]);

  // Si se pasó el prop `parteDiario` (por ejemplo al abrir desde la lista en un modal), usarlo.
  // En caso contrario, si estamos en modo edición por URL, cargar desde la API.
  useEffect(() => {
    if (parteDiario) {
      const d = parteDiario;
      setInitialData({
        id: d.id,
        equipo: d.equipo?.id || d.equipo || '',
        fecha: d.fecha || hoyISO,
        // Preferir horas si > 0 (equipo Hs), si no usar km (equipo Km).
        // No usar ?? porque ambos campos son no-nulos en el modelo (pueden ser 0).
        lectura_contador: d.horas > 0 ? d.horas : (d.kilometros > 0 ? d.kilometros : ''),
        centro_costo: d.centro_costo?.id || d.centro_costo || '',
        obra: d.obra?.id || d.obra || '',
        sector: d.sector?.id || d.sector || '',
        operador: d.operador || '',
        tareas: d.tareas || '',
        observaciones: d.observaciones || ''
      });
      return;
    }

    if (!isEditing && initialEquipo) {
      setInitialData((prev) => ({ ...(prev || {}), equipo: String(initialEquipo), fecha: (prev && prev.fecha) || hoyISO }));
      return;
    }

    if (!isEditing) return;

    const load = async () => {
      try {
        const resp = await apiClient.get(`partes-diarios/${id}/`);
        const d = resp.data || {};
        setInitialData({
          id: d.id,
          equipo: d.equipo?.id || d.equipo || '',
          fecha: d.fecha || hoyISO,
          lectura_contador: d.horas > 0 ? d.horas : (d.kilometros > 0 ? d.kilometros : ''),
          centro_costo: d.centro_costo?.id || d.centro_costo || '',
          obra: d.obra?.id || d.obra || '',
          sector: d.sector?.id || d.sector || '',
          operador: d.operador || '',
          tareas: d.tareas || '',
          observaciones: d.observaciones || ''
        });
      } catch (error) {
        console.error('Error cargando parte diario:', error);
      }
    };

    load();
  }, [parteDiario, id, isEditing, hoyISO, initialEquipo]);

  // Default fecha to today on create
  useEffect(() => {
    if (!isEditing) {
      // only set default fecha if there is no meaningful initial data yet
      setInitialData(prev => {
        try {
          if (prev && Object.keys(prev).length > 0) return prev;
        } catch {}
        return { ...(prev || {}), fecha: hoyISO };
      });
    }
  }, [isEditing, hoyISO]);

  // If editing with a preselected equipo, load equipo metadata to set the unit label
  useEffect(() => {
    const equipoId = initialData?.equipo;
    if (!equipoMeta && equipoId) {
      (async () => {
        try {
          const eqResp = await apiClient.get(`equipos/${equipoId}/`);
          setEquipoMeta(eqResp.data || null);
        } catch {}
      })();
    }
  }, [initialData?.equipo, equipoMeta]);

  // Cuando el equipo viene preseleccionado (por ejemplo desde DetalleEquipo),
  // completar obra y centro de costo aunque no haya interacción manual.
  useEffect(() => {
    const equipoId = initialData?.equipo;
    if (!equipoId) return;

    const hayObra = Boolean(initialData?.obra);
    const hayCentro = Boolean(initialData?.centro_costo);
    if (hayObra && hayCentro) return;

    let cancelled = false;
    const completarDesdeEquipo = async () => {
      try {
        const eqResp = await apiClient.get(`equipos/${equipoId}/`);
        if (cancelled) return;
        const eq = eqResp.data || {};
        const obraEquipo = getObraId(eq);
        const centroPreferido = getCentroCostoPreferidoDesdeEquipo(eq);
        setInitialData(prev => ({
          ...(prev || {}),
          ...(obraEquipo ? { obra: obraEquipo } : {}),
          ...(centroPreferido ? { centro_costo: centroPreferido } : {}),
          ...((obraEquipo && String(prev?.obra || '') !== String(obraEquipo)) ? { sector: '' } : {}),
        }));
      } catch (e) {
        // si falla, el usuario puede completar manualmente
      }
    };

    completarDesdeEquipo();
    return () => { cancelled = true; };
  }, [initialData?.equipo, initialData?.obra, initialData?.centro_costo]);

  // Reglas de validación
  const validationRules = {
    equipo: { required: true },
    fecha: { required: true },
    lectura_contador: { required: true },
    centro_costo: { required: true },
    operador: { required: true }
  };

  // Manejar guardado exitoso
  const handleSave = (savedData, opts = {}) => {
    if (opts.keepOpen) return; // "Guardar y nuevo/duplicar": el formulario se queda abierto
    console.log('Parte diario guardado:', savedData);
    // Volver a la lista de partes diarios
    navigate('/partes-diarios');
  };

  // Transformar payload antes de enviar: mapear lectura_contador a kilómetros u horas según contador
  const transformSubmitData = (data) => {
    const out = { ...data };
    const isHs = (equipoMeta?.contador_nombre || '').toLowerCase().startsWith('h');
    if ('lectura_contador' in out) {
      if (isHs) {
        out.horas = out.lectura_contador;
        out.kilometros = out.kilometros !== undefined ? out.kilometros : 0;
      } else {
        out.kilometros = out.lectura_contador;
        out.horas = out.horas !== undefined ? out.horas : 0;
      }
      delete out.lectura_contador;
    }
    // Asegurar fecha por defecto hoy si viene vacía
    if (!out.fecha) out.fecha = hoyISO;
    return out;
  };

  // Hook al cambiar campos: al elegir equipo, traer detalle para mostrar unidad correcta
  const onFieldChange = async (name, value, currentData, applyUpdates) => {
    if (name === 'equipo' && value) {
      try {
        // Cargar detalle de equipo para conocer el tipo de contador
        const eqResp = await apiClient.get(`equipos/${value}/`);
        const eq = eqResp.data || {};
        setEquipoMeta(eq);
        const obraEquipo = getObraId(eq);
        const centroPreferido = getCentroCostoPreferidoDesdeEquipo(eq);
        const updates = {};
        if (obraEquipo) {
          updates.obra = obraEquipo;
          if (String(currentData?.obra || '') !== String(obraEquipo)) {
            updates.sector = '';
          }
        }
        if (centroPreferido) {
          updates.centro_costo = centroPreferido;
        }
        return updates;
      } catch (e) {
        // ignore errors; user can continue editing manually
      }
    }
    if (name === 'equipo' && !value) {
      return { obra: '', sector: '', centro_costo: '' };
    }
    if (name === 'obra') {
      const previousObra = currentData?.obra;
      const updates = {};
      if (String(previousObra || '') !== String(value || '')) {
        updates.sector = '';
      }
      if (!value) {
        updates.centro_costo = '';
        return updates;
      }
      try {
        updates.centro_costo = await fetchCentroCostoId(apiClient, 'obras', value);
      } catch (e) {
        updates.centro_costo = '';
      }
      return updates;
    }
  };

  // Manejar cancelación
  const handleCancel = () => {
    navigate('/partes-diarios');
    return true;
  };

  return (
    <FormularioBase
      title={isEditing ? 'Editar Parte Diario' : 'Nuevo Parte Diario'}
      icon="bi bi-journal"
      fields={fields}
      initialData={initialData}
      apiEndpoint="partes-diarios/"
      isEditing={isEditing}
      validationRules={validationRules}
      onSave={parentOnSave || handleSave}
      onCancel={parentOnCancel || handleCancel}
      showTitle={showTitle}
      noNestedScroll={noNestedScroll}
      compact={compact}
      fullWidth={fullWidth}
      transformSubmitData={transformSubmitData}
      onFieldChange={onFieldChange}
      errorKeyMap={{ horas: 'lectura_contador', kilometros: 'lectura_contador' }}
      transformFetchData={(apiData) => ({
        lectura_contador: apiData.horas > 0 ? apiData.horas : (apiData.kilometros > 0 ? apiData.kilometros : '')
      })}
      enableSaveAndNew={enableSaveAndNew}
    />
  );
};

export default FormularioParteDiario;
